import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Row, Col, Button, Badge, Spinner } from "reactstrap";
import Head from "../../../layout/head/Head";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Descriptions,
  Modal,
  Popover,
  Empty,
} from "antd";
import { PaperClipOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { formatDecimal } from "../../../utils/Utils";

interface SelectOptions {
  label: string;
  value: string;
}

const { Panel } = AntCollapse;

const WorkBoqPreview: React.FC = (props: any) => {
  const navigate = useNavigate();
  const { handleSubmit, watch, getValues } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const formData = watch();

  const workLevelLabels: { [key: string]: string } = {
    Project: "Project Level",
    Tower: "Tower Level", 
    Villa: "Villa Level",
    Floor: "Floor Level",
    Flat: "Flat Level",
  };

  const completionAlertLabel = formData.completionAlertsFrom
    ? `${formData.completionAlertsFrom}%`
    : "Not Set";

  // Work order lines table columns
  const [columns, setColumns] = useState<TableColumnsType<any>>([
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
      fixed: "left",
    },
    {
      title: "Sub Category",
      dataIndex: "workSubCategory",
      key: "workSubCategory",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (text, record) => `${formatDecimal(text)} ${record?.uom}`,
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      hidden: !["Tower", "Villa", "Floor", "Flat"].includes(formData.workLevel),
      render: (text, record) => {
        const location = [];
        if (record.towerVilla) location.push(record.towerVilla);
        if (record.floorName) location.push(record.floorName);
        if (record.flatName) location.push(record.flatName);
        return location.join(" / ") || "-";
      },
    },
    {
      title: "Materials",
      key: "materials",
      width: 100,
      render: (text, record) => (
        <Badge color="outline-info">
          {record.allocatedMaterials?.length || 0} items
        </Badge>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 100,
      render: (text, record) => (
        <Badge 
          color={record.uploadedDocs?.length > 0 ? "outline-success" : "outline-danger"}
        >
          {record.uploadedDocs?.length > 0 ? "Yes" : "No"}
        </Badge>
      ),
    },
  ]);

  const submitWorkBoq = async (data: any) => {
    try {
      setIsSubmitting(true);
      
      // Prepare payload for API submission
      const payload = {
        boqTitle: data.boqTitle,
        boqDescription: data.boqDescription,
        siteId: data.siteId,
        projectId: data.projectId,
        contractorId: data.contractorId,
        completionAlertsFrom: data.completionAlertsFrom,
        materialBoqId: data.materialBoqId,
        workLevel: data.workLevel,
        workOrderLines: data.workOrderLines || [],
        status: "Draft",
        createdAt: new Date().toISOString(),
        createdBy: "current_user",
      };

      console.log("Work BOQ Submission Payload:", payload);

      // Simulate API call - replace with actual API integration
      await new Promise((resolve) => setTimeout(resolve, 2000));

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Work BOQ has been created successfully and saved as Draft",
        showConfirmButton: true,
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/work-boq-list");
      });
    } catch (error) {
      console.error("Error submitting Work BOQ:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create Work BOQ. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  return (
    <>
      <Head title="Work BOQ Preview" />
      <div className="content clearfix">
        <form onSubmit={handleSubmit(submitWorkBoq)}>
          <AntCollapse defaultActiveKey={["1", "2"]}>
            <Panel
              header={
                <div className="d-flex align-items-center justify-content-between">
                  <>General Information</>
                </div>
              }
              key="1"
            >
              <Col className="justify-start w-100 flex-wrap mt-1">
                <div className="w-30 border-end">
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">BOQ Title</div>
                    <div className="data-value w-70">
                      {formData.boqTitle || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Site</div>
                    <div className="data-value w-70">
                      {formData.siteLabel || formData.siteId || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Project</div>
                    <div className="data-value w-70">
                      {formData.projectLabel || formData.projectId || "--"}
                    </div>
                  </div>
                  {/* <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Validation</div>
                    <div className="data-value w-70">
                      <ValidationSummaryContent />
                    </div>
                  </div> */}
                </div>
                <div className="w-30 border-end paddingLeft10">
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Contractor</div>
                    <div className="data-value w-70">
                      {formData.contractorLabel || formData.contractorId || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Material BOQ</div>
                    <div className="data-value w-70">
                      {formData.materialBoqLabel || formData.materialBoqId || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Work Level</div>
                    <div className="data-value w-70">
                      <Badge color="outline-primary">
                        {workLevelLabels[formData.workLevel] || formData.workLevel || "--"}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="w-30 paddingLeft10">
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Description</div>
                    <div className="data-value w-70">
                      <Popover
                        content={
                          <div>
                            <p>{formData.boqDescription}</p>
                          </div>
                        }
                        title={"BOQ Description"}
                      >
                        {formData.boqDescription?.length > 30 ? (
                          <span
                            style={{
                              color: "#2263b3",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {formData.boqDescription.substring(0, 30)}...
                          </span>
                        ) : (
                          formData.boqDescription || "--"
                        )}
                      </Popover>
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Completion Alert</div>
                    <div className="data-value w-70">
                      <Badge color="outline-info">
                        {completionAlertLabel}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>
            </Panel>
            
            <Panel header="Work Order Lines" key="2">
              <div className="table-responsive poTable">
                <div
                  style={{
                    width: screenSize - 170 + "px",
                    margin: "auto",
                  }}
                >
                  {!formData.workOrderLines || formData.workOrderLines.length === 0 ? (
                    <div className="text-center py-5">
                      <Empty description="No work order lines added" />
                    </div>
                  ) : (
                    <Table
                      className="customTable"
                      columns={columns}
                      dataSource={formData.workOrderLines}
                      scroll={{ x: 1000 }}
                      expandable={{
                        expandedRowRender: (record: any) => (
                          <div className="expandableBody">
                            <div style={{ flex: 1, textAlign: "left" }}>
                              {record.taskDescription && (
                                <div className="mb-3">
                                  <strong>Task Description:</strong>
                                  <p style={{ margin: "5px 0" }}>{record.taskDescription}</p>
                                </div>
                              )}
                              
                              {record.allocatedMaterials?.length > 0 && (
                                <div className="mb-3">
                                  <strong>Allocated Materials:</strong>
                                  <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                                    {record.allocatedMaterials.map((material: any, index: number) => (
                                      <li key={index}>{material.materialName || material.name}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {record.uploadedDocs?.length > 0 && (
                                <div>
                                  <strong>Documents:</strong>
                                  {record.uploadedDocs.map((doc: any, index: number) => (
                                    <div key={index} style={{ marginLeft: "10px" }}>
                                      <PaperClipOutlined /> &nbsp;
                                      {doc.name}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      }}
                      bordered
                      size="small"
                      pagination={false}
                    />
                  )}
                </div>

                {formData.workOrderLines?.length > 0 && (
                  <Row style={{ margin: "20px" }}>
                    <Col className="justify-end">
                      <div className="w-40">
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Total Work Lines</strong>
                          </div>
                          <span className="r_align">
                            {formData.workOrderLines.length}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Total Quantity</strong>
                          </div>
                          <span className="r_align">
                            {formatDecimal(
                              formData.workOrderLines.reduce(
                                (total: number, line: any) =>
                                  total + (Number(line.quantity) || 0),
                                0
                              )
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Work Categories</strong>
                          </div>
                          <span className="r_align">
                            {new Set(
                              formData.workOrderLines.map(
                                (line: any) => line.workCategory
                              )
                            ).size}
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Panel>
          </AntCollapse>


          <div className="actions clearfix">
            <ul>
              <li>
                {isSubmitting ? (
                  <Button color="primary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button 
                    color="primary" 
                    type="submit"
                    disabled={
                      !formData.boqTitle ||
                      !formData.siteId ||
                      !formData.materialBoqId ||
                      !formData.workLevel ||
                      !formData.workOrderLines?.length
                    }
                  >
                    Submit Work BOQ
                  </Button>
                )}
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={() => {
                    props.prev();
                  }}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
            </ul>
          </div>
        </form>
      </div>
    </>
  );
};

export default WorkBoqPreview;